﻿import {isNullOrEmpty, NamedValue} from "@2d-front-monorepo/core";
import React, {ChangeEvent, JSX, useCallback} from "react";
import {useControlId} from "../helpers/useControlId";
import Select from "../HtmlControls/Select";
import {MvvmControlProps} from "./MvvmControlProps";
import {useItemMap} from "./useItemMap";
import {useRegisterValidation} from "./useRegisterValidation";
import {useSelectedItem} from "./useSelectedItem";

export type MvvmSelectProps<T extends object, TValue> = MvvmControlProps<T, TValue> & {
	items: NamedValue<TValue>[];
};

export default function MvvmSelect<T extends object = never, TValue = never>(props: MvvmSelectProps<T, TValue>): JSX.Element {
	const {
		context,
		property,
		id,
		label,
		hint,
		column,
		hidden,
		disabled,
		validation,
		items,
		placeholder
	} = props;

	const controlId = useControlId(id);

	const itemMap = useItemMap(items);
	const selected = useSelectedItem(items, context.getValue<TValue>(property));
	const hasPlaceholder = !isNullOrEmpty(placeholder);

	const onChange = useCallback(function (e: ChangeEvent<HTMLSelectElement>): void {
		const index = e.target.selectedIndex;
		if (hasPlaceholder && index === 0) {
			context.setValue(property, undefined, true);
		} else {
			context.setValue(property, items.at(hasPlaceholder ? index - 1 : index)?.value, true);
		}

	}, [context, property, validation, controlId, hasPlaceholder]);

	useRegisterValidation(context, property, controlId, validation);

	return <Select label={label}
	               name={property}
	               items={itemMap}
	               value={selected}
	               placeholder={placeholder}
	               hint={hint}
	               hidden={hidden}
	               column={column}
	               id={id}
	               required={validation?.required != null}
	               error={context.getError(property)?.error}
	               disabled={disabled}
	               onChange={onChange}
	/>;
};
